import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Tooltip,
  IconButton,
  Collapse
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { LEAD_THEME as T, getStageConfig, getStatusConfig } from '../../utils/lead_helper_v2';

// Map child apiKey -> status key for getStatusConfig (for sub-item dot color)
const CHILD_KEY_TO_STATUS = {
  won: 'closed_won',
  lost: 'lost',
  assigned: 'assigned',
  not_interested: 'not_interested',
  not_looking: 'not_looking_currently',
  cold_lead: 'cold_lead',
};
function getConfigForSectionId(sectionId) {
  return getStageConfig(sectionId);
}
function getConfigForChildId(childId) {
  const parts = childId.split('_');
  if (parts.length >= 2) {
    const stageKey = parts[0];
    const subKey = parts.slice(1).join('_');
    if (subKey === 'all') return getStageConfig(stageKey);
    const statusKey = CHILD_KEY_TO_STATUS[subKey];
    if (statusKey) return getStatusConfig(statusKey);
    return getStageConfig(stageKey);
  }
  return getStageConfig(parts[0]);
}

const FILTER_CONFIGURATIONS = {
  'all': {
    filters: {},
    label: 'All Leads',
    type: 'main',
    stage: 'all'
  },
  'new': {
    filters: { lead_stage: ['new'] },
    label: 'New',
    type: 'main',
    stage: 'new'
  },
  'unassigned': {
    filters: { unassigned: true },
    label: 'Unassigned',
    type: 'main',
    stage: 'unassigned'
  },
  'new_all': {
    filters: { lead_stage: ['new'] },
    label: 'All new',
    type: 'sub',
    parent: 'new',
    stage: 'new',
    apiFilterKey: 'all'
  },
  'new_assigned': {
    filters: {
      lead_stage: ['new'],
      status: ['assigned'],
      last_contacted_at__isnull: true
    },
    label: 'Not contacted',
    type: 'sub',
    parent: 'new',
    stage: 'new',
    apiFilterKey: 'assigned'
  },
  'contacted': {
    filters: { lead_stage: ['contacted'] },
    label: 'Contacted',
    type: 'main',
    stage: 'contacted'
  },
  'qualified': {
    filters: { lead_stage: ['qualified', 'follow_up'] },
    label: 'Qualified',
    type: 'main',
    stage: 'qualified'
  },
  'site_visit': {
    filters: { lead_stage: ['site_visit'] },
    label: 'Site visit',
    type: 'main',
    stage: 'site_visit'
  },
  'negotiation': {
    filters: { lead_stage: ['negotiation'] },
    label: 'Negotiation',
    type: 'main',
    stage: 'negotiation'
  },
  'closure': {
    filters: { lead_stage: ['closure'] },
    label: 'Closure',
    type: 'main',
    stage: 'closure'
  },
  'closure_won': {
    filters: { lead_stage: ['closure'], status: ['closed_won'] },
    label: 'Won',
    type: 'sub',
    parent: 'closure',
    stage: 'closure',
    apiFilterKey: 'closed_won'
  },
  'closure_lost': {
    filters: { lead_stage: ['closure'], status: ['lost'] },
    label: 'Lost',
    type: 'sub',
    parent: 'closure',
    stage: 'closure',
    apiFilterKey: 'lost'
  },
  'junk': {
    filters: { lead_stage: ['junk'] },
    label: 'Junk',
    type: 'main',
    stage: 'junk'
  },
  'junk_all': {
    filters: { lead_stage: ['junk'] },
    label: 'All junk',
    type: 'sub',
    parent: 'junk',
    stage: 'junk',
    apiFilterKey: 'all'
  },
  'junk_not_interested': {
    filters: { lead_stage: ['junk'], status: ['not_interested'] },
    label: 'Not interested',
    type: 'sub',
    parent: 'junk',
    stage: 'junk',
    apiFilterKey: 'not_interested'
  },
  'junk_not_looking': {
    filters: { lead_stage: ['junk'], status: ['not_looking_currently'] },
    label: 'Not looking',
    type: 'sub',
    parent: 'junk',
    stage: 'junk',
    apiFilterKey: 'not_looking_currently'
  },
  'junk_cold_lead': {
    filters: { lead_stage: ['junk'], status: ['cold_lead'] },
    label: 'Cold lead',
    type: 'sub',
    parent: 'junk',
    stage: 'junk',
    apiFilterKey: 'cold_lead'
  },
  'archived': {
    filters: { lead_stage: ['archived'] },
    label: 'Archived',
    type: 'main',
    stage: 'archived'
  },
  'deleted': {
    filters: { is_deleted: true },
    label: 'Deleted',
    type: 'main',
    stage: 'deleted',
    icon: 'delete'
  }
};

export { FILTER_CONFIGURATIONS };

// Preferred section order; any key from API not in this list is appended after.
const PREFERRED_ORDER = ['all', 'new', 'unassigned', 'contacted', 'qualified', 'site_visit', 'negotiation', 'closure', 'junk', 'archived', 'deleted'];

const LeadSidebarV2 = ({
  width = 200,
  collapsed = false,
  onToggleCollapse,
  statusCounts: statusCountsProp,
  selectedStatus = 'all',
  onStatusChange,
  dateRangeState,
  embedded = false,
}) => {
  const [expandedSection, setExpandedSection] = useState(null);

  const handleToggleSection = (sectionId) => {
    setExpandedSection(prev => (prev === sectionId ? null : sectionId));
  };

  useEffect(() => {
    if (selectedStatus && selectedStatus.includes('_')) {
      const parentId = selectedStatus.split('_')[0];
      if (parentId !== expandedSection) {
        setExpandedSection(parentId);
      }
    }
  }, [selectedStatus]);

  // Normalize: API may return { pipeline: { ... }, saved_searches: [] } or flat { new: {...}, all: {...}, saved_searches: [] }
  const statusCounts = useMemo(() => {
    if (!statusCountsProp || typeof statusCountsProp !== 'object') return {};
    if (statusCountsProp.pipeline && typeof statusCountsProp.pipeline === 'object') {
      return { ...statusCountsProp.pipeline, saved_searches: statusCountsProp.saved_searches };
    }
    return statusCountsProp;
  }, [statusCountsProp]);

  // Build pipeline sections entirely from API: section keys and nested options from status-counts response
  const pipelineSections = useMemo(() => {
    const skipKeys = ['saved_searches', 'source'];
    const rawKeys = Object.keys(statusCounts).filter((k) => !skipKeys.includes(k));
    const orderedKeys = [...PREFERRED_ORDER.filter((k) => rawKeys.includes(k)), ...rawKeys.filter((k) => !PREFERRED_ORDER.includes(k))];

    return orderedKeys.map((stageKey) => {
      const stageData = statusCounts[stageKey];
      if (stageData == null || typeof stageData !== 'object' || Array.isArray(stageData)) return null;

      const label = stageData.label ?? stageKey;
      const count = Number(stageData.count ?? 0);
      const filters = stageData.filters && typeof stageData.filters === 'object' && !Array.isArray(stageData.filters) ? stageData.filters : {};
      const children = Object.entries(filters).map(([apiKey, subData]) => ({
        id: `${stageKey}_${apiKey}`,
        label: subData?.label ?? apiKey,
        count: Number(subData?.count ?? 0),
      }));

      return {
        id: stageKey,
        label,
        count,
        children,
        icon: stageKey === 'deleted' ? 'delete' : FILTER_CONFIGURATIONS[stageKey]?.icon,
      };
    }).filter(Boolean);
  }, [statusCounts]);

  const handleStatusFilter = (statusId) => {
    if (onStatusChange) {
      onStatusChange(statusId);
    }
  };

  const toggleButtonStyles = {
    position: 'absolute',
    top: '10px',
    right: '-12px',
    width: '24px',
    height: '24px',
    backgroundColor: '#fff',
    border: '1px solid',
    borderColor: 'rgba(0,0,0,0.1)',
    borderRadius: '50%',
    boxShadow: '0px 1px 3px rgba(0,0,0,0.1)',
    zIndex: 10,
    padding: 0,
    '&:hover': { backgroundColor: '#f8f9fa' }
  };

  if (!statusCounts) {
    return <div style={{ padding: 16, fontSize: 13, color: '#999' }}>Loading…</div>;
  }

  if (!embedded && collapsed) {
    return (
      <Box sx={{ width: '10px', borderRight: '1px solid', borderColor: 'divider', position: 'relative', height: '100%', bgcolor: '#f8fafc' }}>
        <Tooltip title="Expand sidebar" placement="right">
          <IconButton size="small" onClick={onToggleCollapse} sx={toggleButtonStyles}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: embedded ? '100%' : width,
        borderRight: embedded ? 'none' : '1px solid',
        borderColor: 'divider',
        bgcolor: embedded ? 'transparent' : '#fff',
        transition: 'width 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        height: embedded ? 'auto' : '100vh',
        minHeight: embedded ? 0 : undefined,
        flex: embedded ? '0 0 auto' : undefined,
        overflow: embedded ? 'visible' : undefined,
        boxShadow: embedded ? 'none' : '0px 0px 10px rgba(0,0,0,0.03)',
      }}
    >
      {!embedded && (
        <Tooltip title="Collapse sidebar" placement="right">
          <IconButton size="small" onClick={onToggleCollapse} sx={toggleButtonStyles}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Box sx={{
        mt: '10px',
        flex: embedded ? '0 0 auto' : 1,
        minHeight: 0,
        overflowY: embedded ? 'visible' : 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.15)', borderRadius: '4px' },
        '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
      }}>
        <Typography
          variant="overline"
          sx={{
            px: 1.5,
            pt: 0.5,
            pb: 0.75,
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
            color: T.muted,
          }}
        >
          Pipeline
        </Typography>
        <List sx={{ py: 0.5, mt: 0, '& .MuiListItemButton-root': { borderRadius: 8, mx: 0.75, margin: '1px 6px', transition: 'all 0.2s ease' } }}>
          {pipelineSections.map((section) => {
            const isParentSelected = selectedStatus === section.id;
            const hasChildren = section.children.length > 0;
            const isExpanded = expandedSection === section.id;
            const stageCfg = getConfigForSectionId(section.id);

            return (
              <React.Fragment key={section.id}>
                <ListItemButton
                  selected={isParentSelected && !hasChildren}
                  onClick={() => hasChildren ? handleToggleSection(section.id) : handleStatusFilter(section.id)}
                  sx={{
                    py: 0.75, px: 1.5, pl: 1.5, position: 'relative', minHeight: 36, borderRadius: 8, mb: 0.25, fontWeight: 500,
                    backgroundColor: isParentSelected && !hasChildren ? T.greenSf : 'transparent',
                    borderLeft: isParentSelected && !hasChildren ? `3px solid ${T.green}` : '3px solid transparent',
                    '&.Mui-selected': { bgcolor: T.greenSf, borderLeft: `3px solid ${T.green}`, '&:hover': { bgcolor: 'rgba(10,107,62,0.12)' } },
                    '&:hover': { bgcolor: hasChildren && !isParentSelected ? 'rgba(0,0,0,0.04)' : (isParentSelected && !hasChildren ? T.greenSf : 'rgba(0,0,0,0.04)') },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, flex: 1, minWidth: 0 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: isParentSelected && !hasChildren ? T.green : stageCfg.dot, flexShrink: 0 }} />
                    <ListItemText
                      primary={
                        section.icon === 'delete' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span>{section.label}</span>
                          </Box>
                        ) : section.label
                      }
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: isParentSelected && !hasChildren ? 700 : 500,
                        color: isParentSelected && !hasChildren ? T.green : T.ink2,
                        fontSize: '0.8125rem',
                        noWrap: true,
                      }}
                      sx={{ m: 0 }}
                    />
                  </Box>
                  {hasChildren && (
                    isExpanded
                      ? <ExpandLessIcon fontSize="small" sx={{ color: T.muted, ml: 0.5 }} />
                      : <ExpandMoreIcon fontSize="small" sx={{ color: T.muted, ml: 0.5 }} />
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      ml: 0.5, minWidth: 22, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      borderRadius: 99, padding: '1px 7px', fontSize: 11, fontWeight: 700,
                      backgroundColor: isParentSelected && !hasChildren ? T.greenLt : T.bg,
                      color: isParentSelected && !hasChildren ? T.green : T.muted,
                    }}
                  >
                    {section.count ?? 0}
                  </Typography>
                </ListItemButton>

                {hasChildren && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ pl: 1, pr: 0 }}>
                      {section.children.map((child) => {
                        const isChildSelected = selectedStatus === child.id;
                        const childCfg = getConfigForChildId(child.id);
                        return (
                          <ListItemButton
                            key={child.id}
                            selected={isChildSelected}
                            onClick={() => handleStatusFilter(child.id)}
                            sx={{
                              py: 0.5, pl: 1.5, pr: 1.5, margin: '1px 6px', borderRadius: 8, minHeight: 28,
                              backgroundColor: isChildSelected ? T.greenSf : 'transparent',
                              borderLeft: isChildSelected ? `3px solid ${T.green}` : '3px solid transparent',
                              '&.Mui-selected': { bgcolor: T.greenSf, borderLeft: `3px solid ${T.green}`, '&:hover': { bgcolor: 'rgba(10,107,62,0.12)' } },
                              '&:hover': { bgcolor: isChildSelected ? T.greenSf : 'rgba(0,0,0,0.04)' },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, flex: 1, minWidth: 0 }}>
                              <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: isChildSelected ? T.green : childCfg.dot, flexShrink: 0 }} />
                              <ListItemText
                                primary={child.label}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  fontWeight: isChildSelected ? 700 : 500,
                                  color: isChildSelected ? T.green : T.ink2,
                                  fontSize: '0.8125rem',
                                  noWrap: true,
                                }}
                                sx={{ m: 0 }}
                              />
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                ml: 0.5, minWidth: 22, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                borderRadius: 99, padding: '1px 7px', fontSize: 11, fontWeight: 700,
                                backgroundColor: isChildSelected ? T.greenLt : T.bg,
                                color: isChildSelected ? T.green : T.muted,
                              }}
                            >
                              {child.count ?? 0}
                            </Typography>
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};

export default LeadSidebarV2;
